import React, { useState } from "react";

import { faFile } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import type { AskSource } from "@performyard/service-contracts";

type SourcesBoxProps = {
	sources: AskSource[];
	onSourceClick: (s3FileId: string, filename: string) => void;
	/** "panel" drops the bordered grey box for the docked panel's chrome-less AI message (node 187:29891). */
	variant?: "floating" | "panel";
};

export const SourcesBox: React.FC<SourcesBoxProps> = ({ sources, onSourceClick, variant = "floating" }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const isPanel = variant === "panel";

	if (sources.length < 2) {
		return null;
	}

	return (
		<div
			className={
				isPanel
					? "mt-2 flex w-full flex-col gap-1"
					: "mt-2 flex w-full flex-col overflow-hidden rounded-[4px] border border-solid border-[#E9EAEC] bg-[#F5F5F5]"
			}
		>
			{/* Header */}
			<button
				type="button"
				onClick={() => setIsExpanded(!isExpanded)}
				className={
					isPanel
						? "box-border flex h-4 cursor-pointer appearance-none items-center gap-1 self-start rounded-sm border-0 bg-transparent p-0 [box-shadow:none] outline-hidden hover:underline"
						: "box-border flex w-full appearance-none items-center justify-between rounded-t border-0 border-b border-solid border-[#E9EAEC] bg-[#F5F5F5] px-2 py-1 [box-shadow:none] outline-hidden hover:bg-neutral-200"
				}
			>
				<span className={isPanel ? "text-xs leading-4 text-primary-5" : "text-xs leading-4 text-[#525861]"}>
					Sources ({sources.length})
				</span>
				<FontAwesomeIcon
					icon={isExpanded ? faChevronUp : faChevronDown}
					className={isPanel ? "h-3 w-[9px] shrink-0 text-primary-5" : "h-3 w-[9px] shrink-0 text-[#525861]"}
				/>
			</button>

			{/* Sources List */}
			{isExpanded && (
				<div
					className={
						isPanel
							? "flex w-full flex-col gap-1 overflow-hidden px-2 py-1"
							: "flex w-full flex-col gap-1 overflow-hidden bg-[#F5F5F5] px-2 py-1"
					}
				>
					{sources.map((source) => (
						<button
							key={source.s3FileId}
							type="button"
							onClick={() => onSourceClick(source.s3FileId, source.filename)}
							className={
								isPanel
									? "box-border flex w-full min-w-0 cursor-pointer appearance-none items-start gap-1 border-0 bg-transparent py-0.5 text-left text-primary-5 [box-shadow:none] outline-hidden hover:!text-primary-7 hover:underline"
									: "box-border flex w-[95%] min-w-0 cursor-pointer appearance-none items-center gap-1 overflow-hidden border-0 bg-transparent py-0.5 text-left text-primary-5 [box-shadow:none] outline-hidden hover:!text-primary-7 hover:underline"
							}
						>
							<FontAwesomeIcon icon={faFile} className="h-4 w-4 shrink-0" />
							{/* Panel wraps the full filename at regular weight; floating keeps the
							    single-line semibold label the FAB ships with. */}
							<span
								className={
									isPanel
										? "min-w-0 flex-1 text-xs leading-4 break-words"
										: "w-0 flex-grow truncate text-xs leading-4 font-semibold"
								}
							>
								{source.filename}
							</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
};

export default SourcesBox;
